if (!customElements.get('jersey-print-preview')) {
  customElements.define(
    'jersey-print-preview',
    class JerseyPrintPreview extends HTMLElement {
      // Reference coordinate space (the standard composite layout), drawn at 2x
      // into the canvas so the browser downscales it like a raster image —
      // keeps the hairline cuts in the MEX digits visible at mobile sizes.
      // Name styling mirrors app.sportmonda.com (letter-spacing 4, tighter
      // spacing for longer names), which is also what the baked print on the
      // original product images was made with.
      static REF_WIDTH = 1372;
      static NAME = { x: 968, size: 44, maxWidth: 340 };
      static NUMBER = { x: 967, size: 300, spacing: '-8px' };

      // Print position per composite layout (set via data-layout, derived
      // from the media alt text: 'livepreview' = kit, 'livepreview-shirt' =
      // shirt). The shirt-only composites place the same-size print 144px
      // lower than the kit composites.
      static LAYOUTS = {
        kit: { nameBaseline: 332, numberBaseline: 580 },
        shirt: { nameBaseline: 476, numberBaseline: 724 },
      };

      static nameSpacing(length) {
        return length <= 8 ? 4 : length <= 11 ? 2 : 1;
      }

      connectedCallback() {
        this.canvas = this.querySelector('canvas');
        if (!this.canvas) return;
        this.ctx = this.canvas.getContext('2d');
        this.color = getComputedStyle(this).getPropertyValue('--jersey-print-color').trim() || '#ffffff';

        // Static mode (cart line items): fixed values from data attributes.
        // Live mode (product page): synced to the Navn/Nummer inputs.
        this.isStatic = this.hasAttribute('data-name') || this.hasAttribute('data-number');
        if (!this.isStatic) {
          this.nameInput = document.getElementById('name');
          this.numberInput = document.getElementById('number');
          if (this.nameInput) {
            this.nameInput.addEventListener('input', () => this.render());
          }
          if (this.numberInput) {
            this.numberInput.addEventListener('input', () => this.render());
          }
          this.setupMiniPreview();
        }

        this.render();
        Promise.all([
          document.fonts.load(`${JerseyPrintPreview.NUMBER.size}px JerseyMEX`),
          document.fonts.load(`700 ${JerseyPrintPreview.NAME.size}px Rajdhani`),
        ]).then(() => this.render());
      }

      // On mobile the gallery preview is scrolled out of view (and the
      // keyboard covers most of the screen) while typing in the print fields.
      // Float a small cropped copy of the back of the jersey at the top of
      // the visual viewport while either input is focused.
      setupMiniPreview() {
        if (this.closest('.jersey-print-mini')) return; // we ARE the mini
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        if (!this.nameInput && !this.numberInput) return;

        const existing = document.querySelector('.jersey-print-mini');
        if (existing) existing.remove();

        const img = this.querySelector('img');
        if (!img) return;

        const layout = this.dataset.layout || 'kit';
        const mini = document.createElement('div');
        mini.className = `jersey-print-mini jersey-print-mini--${layout}`;
        mini.hidden = true;
        mini.setAttribute('aria-hidden', 'true');

        const preview = document.createElement('jersey-print-preview');
        preview.dataset.layout = layout;
        preview.style.setProperty('--jersey-print-color', this.color);
        const miniImg = document.createElement('img');
        miniImg.src = img.currentSrc || img.src;
        miniImg.alt = '';
        const miniCanvas = document.createElement('canvas');
        miniCanvas.className = 'jersey-print-overlay';
        miniCanvas.width = 1372;
        miniCanvas.height = 1368;
        preview.append(miniImg, miniCanvas);
        mini.append(preview);
        document.body.append(mini);

        // Pin to the top-right of the visual viewport, unless the focused
        // input would end up underneath it (iOS scrolls the input high) —
        // then pin to the bottom-right, just above the keyboard.
        const position = () => {
          const vv = window.visualViewport;
          const offsetTop = vv ? vv.offsetTop : 0;
          const viewHeight = vv ? vv.height : window.innerHeight;
          const height = mini.offsetHeight;
          let visualTop = 8;
          const input = document.activeElement;
          if (input === this.nameInput || input === this.numberInput) {
            const rect = input.getBoundingClientRect();
            const inputVisualTop = rect.top - offsetTop;
            if (inputVisualTop < visualTop + height + 8) {
              visualTop = viewHeight - height - 8;
            }
          }
          mini.style.top = `${visualTop + offsetTop}px`;
        };
        const show = () => {
          mini.hidden = false;
          position();
          if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', position);
            window.visualViewport.addEventListener('scroll', position);
          }
          window.addEventListener('scroll', position, { passive: true });
        };
        const hide = () => {
          mini.hidden = true;
          if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', position);
            window.visualViewport.removeEventListener('scroll', position);
          }
          window.removeEventListener('scroll', position);
        };

        [this.nameInput, this.numberInput].forEach((input) => {
          if (!input) return;
          input.addEventListener('focus', show);
          input.addEventListener('blur', () => {
            // keep visible when hopping between the two fields
            setTimeout(() => {
              const active = document.activeElement;
              if (active !== this.nameInput && active !== this.numberInput) hide();
            }, 150);
          });
        });
      }

      render() {
        const { NAME, NUMBER, LAYOUTS } = this.constructor;
        const layout = LAYOUTS[this.dataset.layout] || LAYOUTS.kit;
        const ctx = this.ctx;
        const scale = this.canvas.width / this.constructor.REF_WIDTH;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.clearRect(0, 0, this.constructor.REF_WIDTH, this.canvas.height / scale);
        ctx.fillStyle = this.color;
        ctx.textAlign = 'center';

        const name = this.isStatic
          ? (this.dataset.name || '').trim().toUpperCase()
          : ((this.nameInput && this.nameInput.value.trim()) || 'NAVN').toUpperCase();
        if (name) {
          if ('letterSpacing' in ctx) ctx.letterSpacing = `${this.constructor.nameSpacing(name.length)}px`;
          ctx.font = `700 ${NAME.size}px Rajdhani, sans-serif`;
          const width = ctx.measureText(name).width;
          if (width > NAME.maxWidth) {
            ctx.font = `700 ${(NAME.size * NAME.maxWidth) / width}px Rajdhani, sans-serif`;
          }
          ctx.fillText(name, NAME.x, layout.nameBaseline);
        }

        const number = this.isStatic
          ? (this.dataset.number || '').replace(/\D/g, '').slice(0, 2)
          : ((this.numberInput && this.numberInput.value.replace(/\D/g, '')) || '10').slice(0, 2);
        if (number) {
          if ('letterSpacing' in ctx) ctx.letterSpacing = NUMBER.spacing;
          ctx.font = `${NUMBER.size}px JerseyMEX, sans-serif`;
          ctx.fillText(number, NUMBER.x, layout.numberBaseline);
        }
      }
    }
  );
}
